import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { gestApi, useGestAuth } from "./GestAuthContext";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import AlertsPanel from "./AlertsPanel";

const money = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

const lightColour = { green: "#16a34a", yellow: "#eab308", red: "#dc2626" };
const lightLabel = { green: "In linea", yellow: "Attenzione", red: "Critico" };

function KpiCard({ label, value, hint, tone = "default" }) {
  const bg = tone === "dark" ? "bg-[#0a1420] text-white" : "bg-white text-[#0a1420]";
  return (
    <div className={`${bg} rounded-2xl p-5 shadow-sm border border-black/[0.04]`}>
      <div className={`text-[11px] uppercase tracking-widest ${tone === "dark" ? "text-white/50" : "text-[#6e6e73]"}`}>{label}</div>
      <div className="mt-1.5 text-[26px] md:text-[30px] font-semibold tracking-[-0.02em]">{value}</div>
      {hint && <div className={`text-[12px] mt-1 ${tone === "dark" ? "text-white/50" : "text-[#6e6e73]"}`}>{hint}</div>}
    </div>
  );
}

export default function GestDashboard() {
  const { canSeeMoney, user } = useGestAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await gestApi.get("/dashboard/overview");
        if (!cancel) setData(data);
      } catch (e) {
        if (cancel) return;
        // 401 → the axios interceptor has already fired the session-expired
        // event; the layout will navigate to /login. Don't surface the raw
        // "Non autenticato" detail as page content.
        if (e?.response?.status === 401) {
          setErr(null);
        } else {
          setErr(e.response?.data?.detail || "Errore nel caricamento della dashboard");
        }
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return <div className="p-8 text-[#6e6e73]">Caricamento…</div>;
  if (err) return <div className="p-8 text-red-600">{err}</div>;
  if (!data) return null;

  const trafficData = [
    { name: "In linea", value: data.totals.traffic.green, colour: lightColour.green },
    { name: "Attenzione", value: data.totals.traffic.yellow, colour: lightColour.yellow },
    { name: "Critico", value: data.totals.traffic.red, colour: lightColour.red },
  ].filter(x => x.value > 0);

  const cantieriChart = data.cantieri.slice(0, 12).map(c => ({
    name: c.nome.length > 14 ? c.nome.slice(0, 12) + "…" : c.nome,
    Produzione: c.kpi.prod_pct,
    Posa: c.kpi.posa_pct,
  }));

  return (
    <div className="p-5 md:p-8 max-w-[1400px] mx-auto" data-testid="gest-dashboard">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[12px] uppercase tracking-widest text-[#6e6e73]">Dashboard</div>
          <h1 className="text-[26px] md:text-[34px] font-semibold tracking-[-0.02em] text-[#0a1420]">Ciao {user?.name?.split(" ")[0]}</h1>
        </div>
      </div>

      {/* KPI top row */}
      <div className={`grid grid-cols-2 ${canSeeMoney ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3 md:gap-4 mb-6`}>
        <KpiCard label="Cantieri attivi" value={data.totals.in_corso} hint={`${data.totals.cantieri} totali`} />
        <KpiCard label="In linea" value={data.totals.traffic.green} hint="Semaforo verde" />
        <KpiCard label="Alert attivi" value={data.totals.alerts || 0} hint={`${data.totals.alerts_high || 0} critici`} tone={data.totals.alerts_high ? "dark" : "default"} />
        {canSeeMoney && (
          <KpiCard label="Margine totale" value={money(data.totals.margine_totale)} hint={`Ricavi ${money(data.totals.ricavi_totali)}`} tone="dark" />
        )}
      </div>

      {/* Alerts feed */}
      {(data.alerts?.length || 0) > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-black/[0.04] overflow-hidden mb-6" data-testid="gest-dashboard-alerts">
          <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
              <div className="text-[13px] font-semibold text-[#0a1420]">Alert attivi ({data.alerts.length})</div>
            </div>
            <div className="text-[11px] text-[#6e6e73]">Cliccabili: portano al cantiere</div>
          </div>
          <AlertsPanel alerts={data.alerts} variant="compact" />
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/[0.04]">
          <div className="text-[13px] font-semibold text-[#0a1420] mb-3">Semaforo cantieri</div>
          {trafficData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={trafficData} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={4}>
                  {trafficData.map((e) => <Cell key={e.name} fill={e.colour} />)}
                </Pie>
                <Legend iconType="circle" verticalAlign="bottom" height={30} formatter={(v, e) => <span className="text-[12px] text-[#6e6e73]">{v} ({e.payload.value})</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-[13px] text-[#6e6e73]">Nessun cantiere attivo</div>}
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/[0.04] lg:col-span-2">
          <div className="text-[13px] font-semibold text-[#0a1420] mb-3">Avanzamento cantieri</div>
          {cantieriChart.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={cantieriChart} barGap={2}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6e6e73" }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11, fill: "#6e6e73" }} />
                <Tooltip formatter={(v) => `${v}%`} contentStyle={{ borderRadius: 8, border: "1px solid #e4e4e7", fontSize: 12 }} />
                <Bar dataKey="Produzione" fill="#a7c8e9" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Posa" fill="#0a1420" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-[13px] text-[#6e6e73]">Ancora nessun avanzamento</div>}
        </div>
      </div>

      {/* Cantieri table */}
      <div className="bg-white rounded-2xl shadow-sm border border-black/[0.04] overflow-hidden" data-testid="gest-dashboard-cantieri-table">
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="text-[13px] font-semibold text-[#0a1420]">Cantieri</div>
          <Link to="/gestione/cantieri" className="text-[12px] text-[#0a1420] hover:underline">Vedi tutti ›</Link>
        </div>
        <div className="divide-y divide-black/5">
          {data.cantieri.length === 0 && <div className="p-6 text-center text-[13px] text-[#6e6e73]">Nessun cantiere ancora creato.</div>}
          {data.cantieri.map((c) => (
            <Link
              key={c.id}
              to={`/gestione/cantieri/${c.id}`}
              className="px-5 py-4 flex items-center gap-4 hover:bg-[#f5f7fb] transition-colors"
              data-testid={`gest-cantiere-row-${c.id}`}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: lightColour[c.kpi.light], boxShadow: `0 0 0 4px ${lightColour[c.kpi.light]}22` }} title={lightLabel[c.kpi.light]} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[14px] font-medium text-[#0a1420] truncate">{c.nome}</div>
                  {(c.kpi.n_alerts || 0) > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100 font-medium">
                      {c.kpi.n_alerts} alert
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-[#6e6e73] truncate">{c.cliente} · {c.responsabile || "—"}</div>
              </div>
              <div className="hidden sm:flex items-center gap-6 text-[12px] text-[#6e6e73]">
                <div><span className="text-[#0a1420] font-semibold">{c.kpi.prod_pct}%</span> prod</div>
                <div><span className="text-[#0a1420] font-semibold">{c.kpi.posa_pct}%</span> posa</div>
                {canSeeMoney && c.kpi.margine_pct !== undefined && (
                  <div><span className="text-[#0a1420] font-semibold">{c.kpi.margine_pct}%</span> margine</div>
                )}
              </div>
              <span className="text-[#0a1420]/40 text-[16px]">›</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
